// Shared dark Material-React-Table styling, mirroring the look of the
// Registrations admin page so MRT surfaces don't clash with the dark
// admin chrome.

export const darkPaperSx = {
  backgroundColor: "rgba(30, 41, 59, 0.5)",
  backgroundImage: "none",
  color: "#e2e8f0",
  border: "1px solid rgba(148,163,184,0.16)",
  borderRadius: "1rem",
  boxShadow: "none",
};

export const darkToolbarSx = {
  backgroundColor: "transparent",
  color: "#e2e8f0",
  borderBottom: "1px solid rgba(148,163,184,0.08)",
  "& .MuiButtonBase-root": { color: "#cbd5e1" },
  "& .MuiInputBase-root": { color: "#e2e8f0" },
  "& .MuiInput-underline:before": { borderBottomColor: "rgba(148,163,184,0.2)" },
  "& .MuiSvgIcon-root": { color: "#94a3b8" },
};

export const darkHeadCellSx = {
  padding: "10px 8px",
  backgroundColor: "rgba(15, 23, 42, 0.6)",
  color: "#cbd5e1",
  borderBottom: "1px solid rgba(148,163,184,0.16)",
  fontWeight: 600,
  fontSize: "0.7rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  "& .MuiBox-root": { color: "#cbd5e1" },
  "& .MuiSvgIcon-root": { color: "#94a3b8" },
};

export const darkBodyCellSx = {
  padding: "8px 8px",
  color: "#e2e8f0",
  fontSize: "0.8rem",
  borderBottom: "1px solid rgba(148,163,184,0.08)",
};

export const darkRowSx = {
  backgroundColor: "transparent",
  "&:hover td": { backgroundColor: "rgba(148,163,184,0.06) !important" },
};

export const darkSearchSx = {
  "& .MuiInputBase-input": { color: "#e2e8f0" },
  "& .MuiInputBase-input::placeholder": { color: "#64748b", opacity: 1 },
};

/* ------------------------------------------------------------------------ *
 * Pinned columns
 * ------------------------------------------------------------------------ */

/** The admin page behind the table — `bg-slate-950` on the page wrapper. */
const PAGE = "#020617";

/** The row hover tint darkRowSx paints, repeated here so the two cannot drift. */
const HOVER = "rgba(148,163,184,0.06)";

/** One flat colour as a background-image layer. */
const layer = (color: string) => `linear-gradient(${color}, ${color})`;

/**
 * What a normal body cell actually looks like: PAGE with darkPaperSx over it, flattened.
 * 0.5 × (30,41,59) + 0.5 × (2,6,23) = (16,24,41).
 *
 * material-react-table needs one opaque colour for the surfaces it paints itself, and takes
 * it from `mrtTheme.baseBackgroundColor`. Left unset it falls back to the MUI palette's
 * `background.default` — and the dark look here is Tailwind and `sx`, not a MUI dark theme,
 * so that is #fafafa and every such surface comes out near-white.
 */
export const darkMrtTheme = { baseBackgroundColor: "#101829" };

/**
 * A pinned cell has to be opaque or the columns scrolling underneath show through it, and
 * material-react-table makes it opaque with a `::before` of its own — `content: ""`,
 * `position: absolute`, 100% × 100%, `z-index: -1` — filled with `mrtTheme.baseBackgroundColor`.
 *
 * That colour defaults to the MUI palette's `background.default`. The dark admin look here is
 * Tailwind and `sx`, not a MUI dark theme, so the palette is the stock light one and the
 * pinned column comes out **near-white** in an otherwise dark table.
 *
 * It also explains why setting a background on the cell does nothing: a pinned cell is
 * `position: sticky` with a z-index, so it forms a stacking context, and a `z-index: -1`
 * child paints above the parent's own background. The pseudo-element is the only thing on
 * screen. So these style the pseudo-element.
 *
 * What they paint is the exact layer stack a normal cell sits on — the page, the translucent
 * paper, then whatever the cell itself adds — so the browser composites it the same way it
 * composites the real thing and the pinned column is indistinguishable, whatever those
 * colours are later changed to.
 */
const stack = (...top: string[]) =>
  [...top, darkPaperSx.backgroundColor, PAGE].map(layer).join(", ");

const pinnedBefore = (...top: string[]) => ({
  backgroundColor: PAGE,
  backgroundImage: stack(...top),
  // MRT adds an inset grey edge to mark the pinned boundary. Dropped: the column should
  // read as part of the table, not as a rail bolted to the side of it.
  boxShadow: "none",
});

export const darkPinnedBodyCellSx = {
  '&[data-pinned="true"]::before': pinnedBefore(),
  // The pseudo-element hides the row hover that darkRowSx puts on the cell, so the pinned
  // column has to repaint it or it is the one column that never lights up.
  'tr:hover &[data-pinned="true"]::before': pinnedBefore(HOVER),
};

export const darkPinnedHeadCellSx = {
  '&[data-pinned="true"]::before': pinnedBefore(darkHeadCellSx.backgroundColor),
};
